/**
 * PostFxPipeline — R3F component for post-processing.
 *
 * Pipeline:  RenderPass → [SSAOPass] → [UnrealBloomPass] → [SMAAPass | FXAAPass] → [OutputPass] → [SharpenPass] → [DitherPass] → screen
 *
 * Tone-mapping strategy:
 *   `gl.toneMapping` is kept at the user's desired value (e.g. ACES Filmic).
 *   Three.js r183 automatically disables per-fragment tone-mapping when
 *   rendering to an offscreen render-target (the EffectComposer path).
 *   The `OutputPass` reads `gl.toneMapping` and applies it exactly once.
 *   When no post-effects are active the pipeline falls back to direct
 *   `gl.render()` where per-fragment tone-mapping is the standard path.
 *
 * NaN safety:
 *   UnrealBloomPass blur and composite shaders are patched to clamp values
 *   to [0, 65504] (half-float max) to prevent black-box flicker from NaN
 *   propagation in HDR scenes.  (ref: three.js#27878 — not yet fixed in r183)
 *
 * @see docs/ims-engine-api/01-architecture.md — Core Layer: PostProcessing
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import type { ViewerSettingsManager } from './ViewerSettingsManager';
import type { ToneMappingMode } from '../api/types/viewerSettings';

// ---------------------------------------------------------------------------
// Output dithering shader — breaks up 8-bit banding in smooth gradients
// ---------------------------------------------------------------------------
// The final canvas is 8-bit RGBA. Smooth HDR gradients (e.g. blurred sky)
// produce visible bands after tone-mapping + sRGB quantisation. Adding
// ±0.5/255 of Interleaved Gradient Noise (IGN) per pixel distributes the
// quantisation error, making banding imperceptible.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CAS-inspired sharpening shader (Contrast Adaptive Sharpening)
// ---------------------------------------------------------------------------
// Placed AFTER OutputPass so it operates on final sRGB values.
// Uses a Laplacian unsharp mask where the blending weight is scaled by the
// local luminance contrast — flat areas get less sharpening (avoids noise
// amplification) while edges get more (improved perceived detail).
// Strength 0 = bypass, 1 = strong sharpening (typical: 0.4–0.7 for FXAA).
// ---------------------------------------------------------------------------

const SharpenShader = {
  name: 'SharpenShader',
  uniforms: {
    tDiffuse: { value: null },
    sharpen: { value: 0.0 },
    resolution: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float sharpen;
    uniform vec2  resolution;
    varying vec2 vUv;

    // Perceptual luminance (BT.709)
    float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

    void main() {
      vec2 px = 1.0 / resolution;
      vec3 c  = texture2D(tDiffuse, vUv).rgb;

      // 4 axis-aligned neighbours
      vec3 n = texture2D(tDiffuse, vUv + vec2( 0.0,  px.y)).rgb;
      vec3 s = texture2D(tDiffuse, vUv + vec2( 0.0, -px.y)).rgb;
      vec3 e = texture2D(tDiffuse, vUv + vec2( px.x,  0.0)).rgb;
      vec3 w = texture2D(tDiffuse, vUv + vec2(-px.x,  0.0)).rgb;

      // Local luminance contrast — low in flat areas, high at edges
      float lc   = luma(c);
      float lMin = min(lc, min(min(luma(n), luma(s)), min(luma(e), luma(w))));
      float lMax = max(lc, max(max(luma(n), luma(s)), max(luma(e), luma(w))));
      // Contrast ratio in [0..1]: 0 = perfectly flat, 1 = maximum contrast
      float contrast = clamp((lMax - lMin) / max(lMax, 0.001), 0.0, 1.0);

      // Unsharp mask: centre - average of cross-neighbours
      vec3 blur   = (n + s + e + w) * 0.25;
      // Scale strength by contrast so flat/noisy areas are left alone
      float weight = sharpen * contrast;
      vec3 result  = clamp(c + weight * (c - blur), 0.0, 1.0);

      gl_FragColor = vec4(result, 1.0);
    }
  `,
};

const DitherShader = {
  name: 'DitherShader',
  uniforms: { tDiffuse: { value: null } },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    varying vec2 vUv;
    // Interleaved Gradient Noise (Jimenez 2014) — deterministic, no flicker,
    // blue-noise-like spectral distribution.
    float ign(vec2 p) {
      return fract(52.9829189 * fract(0.06711056 * p.x + 0.00583715 * p.y));
    }
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      // \u00b10.5/255 dither — just enough to hide banding without visible noise
      color.rgb += (ign(gl_FragCoord.xy) - 0.5) / 255.0;
      gl_FragColor = color;
    }
  `,
};

// ---------------------------------------------------------------------------
// NaN-safe bloom patch  (ref: three.js#27878)
// ---------------------------------------------------------------------------
// Three.js r183 does NOT include this fix natively.  NaN values in HDR
// envmaps, reflectors, or bright materials propagate through the bloom
// Gaussian-blur chain, producing black flicker/blocks on some GPUs.
// Clamping blur and composite outputs to half-float max neutralises this.
// ---------------------------------------------------------------------------

function patchBloomNaN(bloomPass: UnrealBloomPass): void {
  // 1. Clamp separable-blur outputs (5 mip levels × 2 directions)
  const blurMats = (bloomPass as unknown as { separableBlurMaterials: THREE.ShaderMaterial[] })
    .separableBlurMaterials;
  if (blurMats) {
    for (const mat of blurMats) {
      mat.fragmentShader = mat.fragmentShader.replace(
        'gl_FragColor = vec4( diffuseSum, 1.0 );',
        'gl_FragColor = vec4( clamp(diffuseSum, vec3(0.0), vec3(65504.0)), 1.0 );',
      );
      mat.needsUpdate = true;
    }
  }

  // 2. Clamp composite-shader bloom output
  const compositeMat = (bloomPass as unknown as { compositeMaterial: THREE.ShaderMaterial })
    .compositeMaterial;
  if (compositeMat) {
    compositeMat.fragmentShader = compositeMat.fragmentShader.replace(
      'float bloomAlpha = max( bloom.r, max( bloom.g, bloom.b ) );',
      'bloom = clamp(bloom, vec3(0.0), vec3(65504.0));\n\t\t\t\t\t\tfloat bloomAlpha = max( bloom.r, max( bloom.g, bloom.b ) );',
    );
    compositeMat.needsUpdate = true;
  }
}

// ---------------------------------------------------------------------------
// SSAO intensity patch
// ---------------------------------------------------------------------------
// Three.js SSAOPass has no built-in intensity control — the occlusion is
// simply `count / KERNEL_SIZE`, hard-clamped to [0,1].  We inject an
// `intensity` uniform so it can be tuned at runtime.
// ---------------------------------------------------------------------------

function patchSsaoIntensity(ssaoPass: SSAOPass): void {
  const aoMat = (ssaoPass as unknown as { ssaoMaterial: THREE.ShaderMaterial }).ssaoMaterial;
  if (!aoMat) return;

  // Add uniform
  aoMat.uniforms['intensity'] = { value: 1.0 };

  // Inject uniform declaration
  aoMat.fragmentShader = aoMat.fragmentShader.replace(
    'uniform float kernelRadius;',
    'uniform float kernelRadius;\nuniform float intensity;',
  );

  // Multiply occlusion by intensity before output
  aoMat.fragmentShader = aoMat.fragmentShader.replace(
    'occlusion = clamp( occlusion / float( KERNEL_SIZE ), 0.0, 1.0 );',
    'occlusion = clamp( occlusion / float( KERNEL_SIZE ) * intensity, 0.0, 1.0 );',
  );

  aoMat.needsUpdate = true;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toThreeToneMapping(mode: ToneMappingMode): THREE.ToneMapping {
  switch (mode) {
    case 'aces-filmic':
      return THREE.ACESFilmicToneMapping;
    case 'reinhard':
      return THREE.ReinhardToneMapping;
    case 'neutral':
      return THREE.AgXToneMapping;
    case 'linear':
      return THREE.NoToneMapping;
  }
}

// ---------------------------------------------------------------------------
// PostFxPipeline
// ---------------------------------------------------------------------------

export interface PostFxPipelineProps {
  manager: ViewerSettingsManager;
}

export function PostFxPipeline({ manager }: PostFxPipelineProps) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);

  // Re-render when ViewerSettingsManager state changes
  useSyncExternalStore(
    (cb) => manager.subscribe(cb),
    () => manager.version,
  );
  const state = manager.getState();

  const composerRef = useRef<EffectComposer | null>(null);
  const ssaoPassRef = useRef<SSAOPass | null>(null);
  const bloomPassRef = useRef<UnrealBloomPass | null>(null);
  const smaaPassRef = useRef<SMAAPass | null>(null);
  const fxaaPassRef = useRef<ShaderPass | null>(null);
  const sharpenPassRef = useRef<ShaderPass | null>(null);
  const prevSizeRef = useRef({ w: 0, h: 0 });
  // Tracks whether we proactively shrank the composer to 1×1 to free MSAA
  // render-target VRAM while no effects are active (e.g. during pano mode).
  // iOS Safari has a tight WebGL VRAM budget; 4× MSAA render targets at
  // Retina DPR can consume 100–200 MB and trigger context loss when a pano
  // texture is uploaded on top of the existing GLB + IBL allocations.
  const composerShrunkRef = useRef(false);

  // -----------------------------------------------------------------------
  // Build EffectComposer once (recreated only when scene/camera/gl change)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const composer = new EffectComposer(gl);

    // 1. RenderPass — renders scene into linear-HDR render targets.
    //    Three.js r183 auto-sets NoToneMapping for materials when the
    //    render-target is not null, so no per-fragment TM happens here.
    composer.addPass(new RenderPass(scene, camera));

    // 2. SSAOPass — screen-space ambient occlusion for cavity darkening.
    //    Renders its own normal + depth buffers internally.
    //    kernelSize (samples) is a shader define — set at construction time.
    const ssao = new SSAOPass(scene, camera, size.width, size.height, state.ssao.samples);
    ssao.enabled = false;
    ssao.output = SSAOPass.OUTPUT.Default;
    // kernelRadius is the world-space sampling radius
    ssao.kernelRadius = 0.05;
    ssao.minDistance = 0.0001;
    ssao.maxDistance = 0.1;
    patchSsaoIntensity(ssao);
    composer.addPass(ssao);
    ssaoPassRef.current = ssao;

    // 3. UnrealBloomPass — operates on linear HDR values.
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(size.width, size.height),
      0,
      0,
      99, // overridden by the sync effect below
    );
    bloom.enabled = false;
    patchBloomNaN(bloom);
    composer.addPass(bloom);
    bloomPassRef.current = bloom;

    // 4a. SMAAPass — high-quality sub-pixel morphological AA.
    const smaa = new SMAAPass();
    smaa.enabled = false;
    composer.addPass(smaa);
    smaaPassRef.current = smaa;

    // 4b. FXAAPass — lightweight screen-space AA (alternative to SMAA).
    const fxaa = new ShaderPass(FXAAShader);
    fxaa.enabled = false;
    fxaa.uniforms['resolution'].value.set(1 / size.width, 1 / size.height);
    composer.addPass(fxaa);
    fxaaPassRef.current = fxaa;

    // 5. OutputPass — reads gl.toneMapping + gl.toneMappingExposure at
    //    render-time and applies tone-mapping + sRGB conversion once.
    composer.addPass(new OutputPass());

    // 6. SharpenPass — CAS-inspired contrast-adaptive unsharp mask.
    //    Only active when sharpen > 0. Placed after OutputPass so it
    //    operates on final sRGB values (same domain human vision works in).
    const sharpen = new ShaderPass(SharpenShader);
    sharpen.enabled = false;
    sharpen.uniforms['resolution'].value.set(size.width, size.height);
    composer.addPass(sharpen);
    sharpenPassRef.current = sharpen;

    // 7. DitherPass — adds ±0.5/255 IGN dithering to the final image.
    //    Breaks up 8-bit banding in smooth gradients (blurred sky, etc.)
    //    Must be AFTER OutputPass so it operates on the final sRGB values.
    composer.addPass(new ShaderPass(DitherShader));

    // Always enable 4× MSAA on the composer's render targets.
    // Without this, transparent alpha-gradient surfaces (baked shadow planes,
    // glass, etc.) are rasterised without multisampling inside the FBO, which
    // produces visible aliasing / streaks — amplified by bloom blur and SMAA
    // edge-detection.  The Canvas's native `antialias: true` only applies to
    // the default framebuffer, not to offscreen render targets.
    composer.renderTarget1.samples = 4;
    composer.renderTarget2.samples = 4;

    composer.setPixelRatio(gl.getPixelRatio());
    composer.setSize(size.width, size.height);
    composerRef.current = composer;

    return () => {
      composer.dispose();
      composerRef.current = null;
      ssaoPassRef.current = null;
      bloomPassRef.current = null;
      smaaPassRef.current = null;
      fxaaPassRef.current = null;
      sharpenPassRef.current = null;
    };
  }, [scene, camera, gl]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Sync pass enable-states + bloom parameters
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (ssaoPassRef.current) {
      ssaoPassRef.current.enabled = state.ssao.enabled;
      ssaoPassRef.current.kernelRadius = state.ssao.radius;
      // Intensity via our patched uniform (see patchSsaoIntensity)
      const aoMat = (ssaoPassRef.current as unknown as { ssaoMaterial: THREE.ShaderMaterial })
        .ssaoMaterial;
      if (aoMat?.uniforms?.['intensity']) {
        aoMat.uniforms['intensity'].value = state.ssao.intensity;
      }
    }
    if (bloomPassRef.current) {
      bloomPassRef.current.enabled = state.bloom.enabled;
      bloomPassRef.current.strength = state.bloom.strength;
      bloomPassRef.current.radius = state.bloom.radius;
      bloomPassRef.current.threshold = state.bloom.threshold;
    }
    if (smaaPassRef.current) {
      smaaPassRef.current.enabled = state.antialiasing === 'smaa';
    }
    if (fxaaPassRef.current) {
      fxaaPassRef.current.enabled = state.antialiasing === 'fxaa';
    }
    if (sharpenPassRef.current) {
      sharpenPassRef.current.enabled = state.sharpen > 0;
      sharpenPassRef.current.uniforms['sharpen'].value = state.sharpen;
    }
  }, [
    state.ssao.enabled,
    state.ssao.intensity,
    state.ssao.radius,
    state.bloom.enabled,
    state.bloom.strength,
    state.bloom.radius,
    state.bloom.threshold,
    state.antialiasing,
    state.sharpen,
  ]);

  // -----------------------------------------------------------------------
  // Renderer tone mapping + exposure
  // -----------------------------------------------------------------------
  // gl.toneMapping MUST stay at the user's desired value at all times.
  // - Composer path: Three.js auto-NoTM for offscreen RTs; OutputPass reads
  //   gl.toneMapping and applies it once.  No double tone-mapping.
  // - Direct path: per-fragment TM via the renderer's toneMapping value.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional: R3F gl (WebGLRenderer) requires direct property mutation for tone mapping
    gl.toneMapping = toThreeToneMapping(state.toneMapping);
    gl.toneMappingExposure = state.exposure;
  }, [gl, state.toneMapping, state.exposure]);

  // -----------------------------------------------------------------------
  // DPR sync (debounced — rapid DPR changes cause RT reallocation which
  // can exhaust VRAM and trigger GL_OUT_OF_MEMORY / context lost)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (Math.abs(gl.getPixelRatio() - state.dpr) < 0.01) return;

    const timer = setTimeout(() => {
      gl.setPixelRatio(state.dpr);
      composerRef.current?.setPixelRatio(state.dpr);
    }, 150);
    return () => clearTimeout(timer);
  }, [gl, state.dpr]);

  // -----------------------------------------------------------------------
  // Render (priority 1 — takes over R3F's default render loop)
  // -----------------------------------------------------------------------
  useFrame(({ gl: renderer, scene: s, camera: cam }) => {
    if (state.paused) return;

    const composer = composerRef.current;

    // Sync composer size on change — only when not artificially shrunk.
    if (
      composer &&
      !composerShrunkRef.current &&
      (size.width !== prevSizeRef.current.w || size.height !== prevSizeRef.current.h)
    ) {
      prevSizeRef.current = { w: size.width, h: size.height };
      composer.setSize(size.width, size.height);
      // FXAA needs the resolution uniform updated on resize
      if (fxaaPassRef.current) {
        fxaaPassRef.current.uniforms['resolution'].value.set(1 / size.width, 1 / size.height);
      }
      // SharpenPass also uses pixel-space resolution
      if (sharpenPassRef.current) {
        sharpenPassRef.current.uniforms['resolution'].value.set(size.width, size.height);
      }
    }

    // When bloom or SMAA is active, render through the EffectComposer.
    // gl.toneMapping stays at the user's desired value — OutputPass applies it.
    const useComposer =
      composer &&
      (ssaoPassRef.current?.enabled ||
        bloomPassRef.current?.enabled ||
        smaaPassRef.current?.enabled ||
        fxaaPassRef.current?.enabled ||
        sharpenPassRef.current?.enabled);

    if (useComposer) {
      if (composerShrunkRef.current) {
        // Restore full-size MSAA render targets before the first composed frame.
        composer.setSize(size.width, size.height);
        prevSizeRef.current = { w: size.width, h: size.height };
        composerShrunkRef.current = false;
      }
      composer!.render();
    } else {
      if (!composerShrunkRef.current && composer) {
        // No effects active — shrink composer render targets to 1×1 to free
        // MSAA VRAM.  On iOS/Retina this can be 100–200 MB that would otherwise
        // sit idle (e.g. during the entire pano mode session) and cause a
        // WebGL context loss when a pano texture is uploaded on top.
        composer.setSize(1, 1);
        composerShrunkRef.current = true;
      }
      // No effects — render directly with per-fragment tone-mapping.
      renderer.render(s, cam);
    }
  }, 1);

  return null;
}

// ---------------------------------------------------------------------------
// Why PostFX changes the appearance of shadows & transparent surfaces
// ---------------------------------------------------------------------------
//
// This is an inherent limitation of any offscreen post-processing pipeline,
// not a bug in this implementation.
//
// **Direct rendering (no PostFX):**
//   Three.js applies tone-mapping (e.g. ACES Filmic) per-fragment inside
//   each material shader.  Alpha-blended objects (ShadowMaterial, baked
//   shadow planes, glass, etc.) are composited by the GPU's fixed-function
//   blending stage AFTER tone-mapping.  The blending therefore happens in
//   sRGB/tone-mapped space.
//
// **EffectComposer rendering (PostFX active):**
//   Three.js auto-disables per-fragment tone-mapping when the render target
//   is an offscreen FBO (see WebGLPrograms.js, r183).  All geometry —
//   opaque and transparent — is composited in LINEAR space inside the FBO.
//   Only afterwards does OutputPass apply tone-mapping to the combined
//   result.
//
// The difference matters because non-linear tone-mapping curves (ACES,
// Reinhard, AgX, …) do NOT commute with alpha blending:
//
//   tonemap( blend(a, b) )  ≠  blend( tonemap(a), tonemap(b) )
//
// Affected surfaces and how they change:
//
//   Shadows (ShadowMaterial, baked PNG/proc, ContactShadows):
//     • Direct:   ground is tone-mapped (compressed), shadow blends on top
//                 → shadow appears darker / more saturated.
//     • Composer:  shadow blends in linear space, then ACES compresses →
//                 the toe curve lifts dark regions → shadow appears lighter.
//
//   Transparent glass (e.g. headlight covers):
//     • Direct:   glass fragment is tone-mapped, then blended over the
//                 already-compressed background → the tinted glass "blocks"
//                 more light because both sides are compressed.
//     • Composer:  glass blends in full linear HDR range, then ACES
//                 compresses the composite → the glass appears more
//                 transparent because the linear blend preserves more of
//                 the background's luminance before compression.
//
// This is standard behaviour in every engine (Unreal, Unity, Godot) when
// offscreen post-processing is enabled.  There is no way to avoid it
// entirely while using an EffectComposer pipeline.
//
// Mitigation applied:
//   Shadow components (GroundShadowComponent, EnvironmentComponent sun catcher)
//   automatically boost their opacity by a compensation factor when the
//   EffectComposer is active. This counteracts the linear-space wash-out
//   so that shadow intensity is visually consistent regardless of PostFX state.
//   See POSTFX_SHADOW_OPACITY_BOOST in the respective components.
//
// MSAA note:
//   The EffectComposer render targets always use 4× MSAA to prevent
//   aliasing artefacts on alpha-gradient surfaces (shadow planes, glass).
//   The Canvas's native `antialias: true` only applies to the default
//   framebuffer, not to offscreen FBOs.  AntialiasingMode is 'smaa' | 'off':
//     'smaa' = 4× MSAA + sub-pixel morphological post-pass
//     'off'  = 4× MSAA baseline only
// ---------------------------------------------------------------------------
